import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  buildRegimenIntelligence,
  pharmClassLabel,
  type RegimenMedicationInput,
  type RegimenSeverity,
} from "@/lib/pharmacology/regimenIntelligence";
import { AlertTriangle, Brain, Clock, ListChecks } from "lucide-react";

const SEVERITY_STYLE: Record<RegimenSeverity, string> = {
  critico: "border-destructive/60 bg-destructive/10",
  relevante: "border-destructive/40 bg-destructive/5",
  cautela: "border-amber-500/40 bg-amber-500/5",
  monitorar: "border-primary/40 bg-primary/5",
  info: "border-border bg-muted/30",
};

const SEVERITY_LABEL: Record<RegimenSeverity, string> = {
  critico: "Crítico",
  relevante: "Relevante",
  cautela: "Cautela",
  monitorar: "Monitorar",
  info: "Informativo",
};

const BUCKET_LABEL: Record<string, string> = {
  manha: "Manhã",
  tarde: "Tarde",
  noite: "Noite",
  "sem-horario": "Sem horário definido",
};

const AXIS_LABEL: Record<string, string> = {
  sedativa: "Carga sedativa",
  serotoninergica: "Carga serotoninérgica",
  catecolaminergica: "Carga catecolaminérgica",
  "anticolinergica-cognitiva": "Carga anticolinérgica/cognitiva",
};

export function RegimenIntelligencePanel({
  medications,
  patientName,
}: {
  medications: RegimenMedicationInput[];
  patientName?: string | null;
}) {
  const analysis = useMemo(() => buildRegimenIntelligence(medications ?? []), [medications]);

  if (!analysis.activeItems.length) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        Sem tratamento ativo para analisar. Cadastre a medicação real do paciente para liberar a leitura do esquema.
      </Card>
    );
  }

  return (
    <div className="min-w-0 space-y-3">
      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Brain className="h-4 w-4 shrink-0" />
          Leitura do esquema {patientName ? `· ${patientName}` : ""}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {analysis.activeItems.length} tratamento(s) ativo(s). Leitura estrutural do esquema — não é concentração
          plasmática nem substitui bula e julgamento clínico.
        </div>
        <div className="mt-3 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Completude do registro</span>
            <span className="font-medium">{analysis.completeness0_100}%</span>
          </div>
          <Progress value={analysis.completeness0_100} className="h-2" />
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {Array.from(new Set(analysis.activeItems.map((item) => item.pharmClass))).map((cls) => (
            <Badge key={cls} variant="outline" className="text-[11px]">
              {pharmClassLabel(cls)}
            </Badge>
          ))}
        </div>
      </Card>

      {analysis.loads.length ? (
        <Card className="p-4">
          <div className="text-sm font-medium">Cargas farmacodinâmicas somadas</div>
          <div className="mt-3 space-y-3">
            {analysis.loads.map((load) => (
              <div key={load.axis} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span>{AXIS_LABEL[load.axis] ?? load.axis}</span>
                  <span className="font-medium">{load.score0_100}/100</span>
                </div>
                <Progress value={load.score0_100} className="h-2" />
                <div className="break-words text-[11px] text-muted-foreground">{load.contributors.join(" · ")}</div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4 shrink-0" />
          Agenda do dia
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {(["manha", "tarde", "noite", "sem-horario"] as const).map((bucket) => (
            <div key={bucket} className="min-w-0 rounded-md border p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{BUCKET_LABEL[bucket]}</div>
              {analysis.schedule[bucket].length ? (
                <ul className="mt-1.5 space-y-1 text-sm">
                  {analysis.schedule[bucket].map((item) => (
                    <li key={item.id} className="break-words">
                      {item.usualTime ? <span className="text-muted-foreground">{item.usualTime} · </span> : null}
                      {item.label}
                      {item.dose != null ? ` · ${item.dose} ${item.doseUnit ?? ""}` : ""}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-1.5 text-xs text-muted-foreground">—</div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {analysis.findings.length ? (
        <div className="space-y-2">
          {analysis.findings.map((finding) => (
            <Card key={finding.id} className={`min-w-0 border p-4 ${SEVERITY_STYLE[finding.severity]}`}>
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="break-words">{finding.title}</span>
                </div>
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {SEVERITY_LABEL[finding.severity]}
                </Badge>
              </div>
              <div className="mt-1 break-words text-[11px] text-muted-foreground">{finding.involved.join(" · ")}</div>
              <div className="mt-2 break-words text-sm">{finding.interpretation}</div>
              {finding.action ? <div className="mt-2 break-words text-xs text-primary">Conduta: {finding.action}</div> : null}
              {finding.monitor?.length ? (
                <div className="mt-1 break-words text-xs text-muted-foreground">Monitorar: {finding.monitor.join(", ")}</div>
              ) : null}
            </Card>
          ))}
        </div>
      ) : null}

      {analysis.dataGaps.length ? (
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ListChecks className="h-4 w-4 shrink-0" />
            Lacunas que limitam a análise
          </div>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {analysis.dataGaps.slice(0, 12).map((gap) => (
              <li key={gap} className="break-words">
                • {gap}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

export default RegimenIntelligencePanel;
